import { DataGrid, GridColumnProps, LinkColumn, SelectFilterComponent, SelectFloatingFilterComponent, SelectMode, SelectSearchFloatingFilterComponent } from '@roc/ui';
import { observer } from 'mobx-react';
import { Box, Grid, IconButton, ListItemIcon, ListItemText, makeStyles, Menu, MenuItem } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { CloudDownload, Done, MoreVert } from '@material-ui/icons';
import { useServicingTransfersStore } from '../../hooks/useServicingTransfersStore';
import { DateFormat, formatDate } from '@roc/feature-utils';
import { dateFilterProps, filterProps, formatCurrency, noOpComparator } from '@roc/ui/utils';
import { ServicingTransfersToolbar } from './servicingTransfersToolbar';
import { ServicingTransferTabs } from '../../utils/constants';
import { MarkAsBoardedModal } from './markAsBoardedModal';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '65vh',
    overflow: 'hidden',
  },
}));

export const columns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'dealName',
    headerName: 'Deal Name',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'subType',
    headerName: 'Subtype',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'lender',
    headerName: 'Lender',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'amount',
    headerName: 'Amount',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'closingDate',
    headerName: 'Closing Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'initialFundingDate',
    headerName: 'Initial Funding Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: '',
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
    width: 80,
    pinned: 'right',
    lockPinned: true,
  },
];

export const boardedColumns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'dealName',
    headerName: 'Deal Name',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'subType',
    headerName: 'Subtype',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'lender',
    headerName: 'Lender',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'amount',
    headerName: 'Amount',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'closingDate',
    headerName: 'Closing Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'initialFundingDate',
    headerName: 'Initial Funding Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'investor',
    headerName: 'Investor',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'servicer',
    headerName: 'Servicer',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'boardingPackageSentDate',
    headerName: 'Boarding Package Sent Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: '',
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
    width: 80,
    pinned: 'right',
    lockPinned: true,
  },
];

export const ServicingTransfersGrid = observer(() => {
  const classes = useStyles();
  const { servicingTransfersStore } = useServicingTransfersStore();
  const { servicingTransfersGridStore, tab } = servicingTransfersStore;
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [record, setRecord] = useState<any>({});
  const [showMarkAsBoardedModal, setShowMarkAsBoardedModal] = useState(false);

  useEffect(() => {
    servicingTransfersStore.fetchDashboard();
    servicingTransfersGridStore.resetAndFetchGridData();
  }, []);

  useEffect(() => {
    servicingTransfersStore.clearLoanIds();
    onTabChange();
  }, [tab]);

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    setRecord(record);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onTabChange = () => {
    servicingTransfersGridStore.resetAndFetchGridData();
  };

  const closeBoardedModal = () => {
    setShowMarkAsBoardedModal(false);
  }

  const frameworkComponents = {
    statusCellRenderer: ({ value }) => value ?? null,
    selectFloatingFilter: SelectFloatingFilterComponent,
    selectSearchFloatingFilter: SelectSearchFloatingFilterComponent,
    selectFilter: SelectFilterComponent,
    taxCertificateStatusCellRenderer: ({ value }) => value ?? null,
    dateCellRenderer: ({ value }) =>
      formatDate(value, DateFormat.MMDDYYYY) ?? null,
    currencyCellRenderer({ value }) {
      return formatCurrency(value);
    },
    actionCellRenderer: params => {
      return (
        <>
          <IconButton
            onClick={e => {
              openMenu(e, params.node.data);
            }}
          >
            <MoreVert color="primary" />
          </IconButton>
        </>
      );
    },
    linkColumnCellRenderer(params) {
      const row = params.node.data;
      return row?.status === 'NA' ? (
        <span> {params.value} </span>
      ) : (
        <LinkColumn
          value={params.value}
          url={'#'}
          onClick={() => {
            servicingTransfersStore.openModal(row);
            console.log('openModal(row)', row);
          }}
        />
      );
    },
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    servicingTransfersGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    servicingTransfersGridStore.setFilterModel(filterModel);
  };

  const onRowSelected = (params) => {
    servicingTransfersStore.setLoanIds(params?.data?.loanId, params?.node.selected);
  };

  const handleModelUpdated = (params) => {
    params.api.forEachNode((node) => {
      if (servicingTransfersStore.loanIds.includes(node.data?.loanId)) {
        node.setSelected(true);
      }
    });
  };

  return (
    <>
      <Box>
        <Grid item className={classes.gridContainer}>
          <DataGrid
            columns={tab === ServicingTransferTabs.SERVICING_TRANSFER ? columns : boardedColumns}
            rows={servicingTransfersGridStore.gridData.data.rows}
            frameworkComponents={frameworkComponents}
            headerCheckboxSelection={true}
            suppressRowClickSelection={true}
            key={tab}
            toolbar={<ServicingTransfersToolbar />}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            isLoading={servicingTransfersGridStore.isLoading}
            showFilters={servicingTransfersGridStore.showFilters}
            sortModel={servicingTransfersGridStore.sortModel}
            filterModel={servicingTransfersGridStore.filterModel}
            paginationData={servicingTransfersGridStore.gridData.meta}
            setPageNumber={servicingTransfersGridStore.setPageNumber}
            onRowSelected={onRowSelected}
            getRowNodeId={(data) => data.loanId}
            handleModelUpdated={handleModelUpdated}
            selectMode={tab === ServicingTransferTabs.SERVICING_TRANSFER && SelectMode.MULTIPLE}
          />
        </Grid>
        <Menu
          id="menu-appbar"
          getContentAnchorEl={null}
          anchorEl={anchorEl}
          keepMounted
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          open={menuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem
            disabled={!record.loanId}
            onClick={() => {
              setShowMarkAsBoardedModal(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Done fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as Boarding Package Sent</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={!record.loanId}
            onClick={() => {
              const loanIds = record?.loanId ? [record.loanId] : [];
              servicingTransfersStore.downloadServicingTransferReport(loanIds);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <CloudDownload fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Servicing Transfer Report</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
      <MarkAsBoardedModal
        servicingTransfersStore={servicingTransfersStore}
        showMarkAsBoardedModal={showMarkAsBoardedModal}
        closeBoardedModal={closeBoardedModal}
        loanIds={record?.loanId ? [record.loanId] : []}
      />
    </>
  );
});
